import { isChartSettingsV2Enabled, isKPIChartEnhancementsEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import { CLIENT_STATE_KEYS, CLIENT_STATE_VERSIONS, DEFAULT_KPI_DISPLAY_PROPERTIES, DEFAULT_PERCENTAGE_FORMAT, } from '../../client-state-type';
import { BackgroundFormatTypes, ChartType, LegendPositionOptions, TupleType, } from '../../types/prism';
import { isCustomOrTsHostedChart } from '../../utils';
import { createDefaultDataLabelLinkedTo, createDefaultLinkedTo, getFirstAxis, getYColumnIds, } from './common-util';
import { customChartUpgraderFn } from '../custom-chart/customVisual-to-v5';
import { ClientStateV4dot2Schema } from '../types/v4dot2';
const getDefaultTextStyle = (isFirstAxis, firstAxisId, key) => {
    if (isFirstAxis) {
        return {
            isLinked: false,
            linkedTo: null,
        };
    }
    return {
        isLinked: true,
        linkedTo: createDefaultLinkedTo(firstAxisId, key),
    };
};
const getDefaultLabelTextStyle = (isYColumn, isFirstYColumn, firstYColumnId) => {
    if (!isYColumn) {
        return {
            isLinked: false,
            linkedTo: null,
        };
    }
    if (isFirstYColumn) {
        return {
            isLinked: false,
            linkedTo: null,
        };
    }
    return {
        isLinked: true,
        linkedTo: createDefaultDataLabelLinkedTo(firstYColumnId, 'labelTextStyle'),
    };
};
const hasProperties = property => {
    return (property &&
        !!Object.values(property).filter(value => {
            if (value && Array.isArray(value)) {
                return !!value.length;
            }
            return !!value;
        }).length);
};
const updateColumnCustomPropsWithCompareValues = (primaryColumnProperty, customCompareValue, genericCompareValue, isKPIChartEnhancementFeatureEnabled) => {
    if (!isKPIChartEnhancementFeatureEnabled ||
        !primaryColumnProperty ||
        (!customCompareValue && !genericCompareValue)) {
        return;
    }
    let columnCustomProps = {};
    try {
        columnCustomProps = JSON.parse(primaryColumnProperty.columnProperty.customProps || '{}');
    }
    catch (_a) {
        columnCustomProps = {};
    }
    columnCustomProps.customCompare = customCompareValue !== null && customCompareValue !== void 0 ? customCompareValue : null;
    if (!_.isNil(genericCompareValue) && genericCompareValue !== '') {
        columnCustomProps.genericCompareValue = genericCompareValue;
    }
    else {
        delete columnCustomProps.genericCompareValue;
    }
    primaryColumnProperty.columnProperty.customProps = JSON.stringify(columnCustomProps);
};
const createTextStyle = (styleProps, additionalStyles = []) => {
    var _a, _b, _c, _d;
    if (!styleProps) {
        return null;
    }
    const filteredAdditionalStyles = additionalStyles.filter(Boolean);
    const style = {
        color: (_a = styleProps === null || styleProps === void 0 ? void 0 : styleProps.color) !== null && _a !== void 0 ? _a : '',
        size: (_b = styleProps === null || styleProps === void 0 ? void 0 : styleProps.fontSize) !== null && _b !== void 0 ? _b : '',
        isLinked: (_c = styleProps === null || styleProps === void 0 ? void 0 : styleProps.isLinked) !== null && _c !== void 0 ? _c : false,
        linkedTo: (_d = styleProps === null || styleProps === void 0 ? void 0 : styleProps.linkedTo) !== null && _d !== void 0 ? _d : null,
        style: [
            (styleProps === null || styleProps === void 0 ? void 0 : styleProps.boldFontWeight) ? 'bold' : null,
            (styleProps === null || styleProps === void 0 ? void 0 : styleProps.italicFontStyle) ? 'italic' : null,
            (styleProps === null || styleProps === void 0 ? void 0 : styleProps.underlineTextDecoration) ? 'underline' : null,
            (styleProps === null || styleProps === void 0 ? void 0 : styleProps.strikeThrough) ? 'strikeThrough' : null,
            ...filteredAdditionalStyles,
        ].filter(Boolean),
    };
    const hasCustomProperties = style.color ||
        style.size ||
        style.isLinked ||
        style.linkedTo !== null ||
        style.style.length > 0;
    return hasCustomProperties ? style : null;
};
const createTooltipBackgroundStyle = tooltip => {
    var _a, _b, _c, _d;
    if (!tooltip) {
        return null;
    }
    const style = {
        backgroundColor: (_a = tooltip === null || tooltip === void 0 ? void 0 : tooltip.backgroundColor) !== null && _a !== void 0 ? _a : '',
        borderColor: (_b = tooltip === null || tooltip === void 0 ? void 0 : tooltip.borderColor) !== null && _b !== void 0 ? _b : '',
        borderWidth: (_c = tooltip === null || tooltip === void 0 ? void 0 : tooltip.borderWidth) !== null && _c !== void 0 ? _c : '1',
        backgroundOpacity: (_d = tooltip === null || tooltip === void 0 ? void 0 : tooltip.opacity) !== null && _d !== void 0 ? _d : '',
    };
    const hasCustomProperties = style.backgroundColor ||
        style.borderColor ||
        style.borderWidth !== '1' ||
        style.backgroundOpacity;
    return hasCustomProperties ? style : null;
};
const LEGEND_POSITION_V4_v5_MAP = {
    [LegendPositionOptions.BottomLegend]: 'Bottom',
    [LegendPositionOptions.LeftLegend]: 'Left',
    [LegendPositionOptions.RightLegend]: 'Right',
    [LegendPositionOptions.TopLegend]: 'Top',
};
const getChartBackgroundStyle = answerLevelSettings => {
    var _a, _b;
    const style = (_a = answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.chart) === null || _a === void 0 ? void 0 : _a.backgroundStyle;
    const bgColor = (_b = style === null || style === void 0 ? void 0 : style.backgroundColor) !== null && _b !== void 0 ? _b : answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.backgroundColor;
    return {
        backgroundStyle: style || bgColor
            ? {
                ...style,
                backgroundColor: bgColor,
                borderWidth: (style === null || style === void 0 ? void 0 : style.borderColor) ? 1 : 0,
            }
            : null,
    };
};
const getPlotAreaBackgroundStyle = answerLevelSettings => {
    var _a, _b;
    const style = (_a = answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.plotArea) === null || _a === void 0 ? void 0 : _a.backgroundStyle;
    const bgColor = (_b = style === null || style === void 0 ? void 0 : style.plotBackgroundColor) !== null && _b !== void 0 ? _b : answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.plotBackgroundColor;
    return {
        backgroundStyle: style || bgColor
            ? {
                ...style,
                backgroundColor: bgColor,
                borderWidth: (style === null || style === void 0 ? void 0 : style.borderColor) ? 1 : 0,
            }
            : null,
    };
};
export const upgradeV4Dot2ToV5 = (chartviz, customVisualProps, renderPropElements) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    if (isCustomOrTsHostedChart(chartviz.config.chartType)) {
        const upgradedClientState = customChartUpgraderFn(customVisualProps, renderPropElements);
        return upgradedClientState;
    }
    const clientState = _.cloneDeep(chartviz.vizProp);
    let { columnProperties } = clientState || {};
    const { axisProperties, chartProperties } = clientState || {};
    const { chartSpecific = {}, gridLines: gridLinesFlags = {} } = chartProperties || {};
    const { tileType, advancedProperties } = JSON.parse(((_a = chartProperties.chartSpecific) === null || _a === void 0 ? void 0 : _a.customProps) || '{}');
    const { answerLevelSettings, gridlines: gridlinesStyles, regression, tooltip, stackLabelStyles = {}, } = advancedProperties || {};
    const newColumnProperties = {};
    if (!columnProperties || !columnProperties.length) {
        columnProperties = (_b = chartviz.columns) === null || _b === void 0 ? void 0 : _b.map((col) => ({
            columnId: col.column.id,
            columnProperty: {
                __typename: 'ChartVizColumnProperties',
                conditionalFormatting: null,
                dataLabels: null,
                handleMissingValues: null,
                excludeNullValues: null,
                customProps: null,
            },
        }));
    }
    const getColumnColor = (chartViz, columnId, seriesColors) => {
        var _a;
        const column = chartViz.columns.find(col => col.column.id === columnId);
        return (_a = seriesColors === null || seriesColors === void 0 ? void 0 : seriesColors.find(series => {
            var _a;
            return series.serieName === columnId ||
                series.serieName === ((_a = column === null || column === void 0 ? void 0 : column.column) === null || _a === void 0 ? void 0 : _a.name);
        })) === null || _a === void 0 ? void 0 : _a.color;
    };
    const getColumnDataColors = (chartViz, columnId) => {
        var _a;
        const axisConfig = _.first((_a = chartViz === null || chartViz === void 0 ? void 0 : chartViz.config) === null || _a === void 0 ? void 0 : _a.axisConfig) || {};
        const sliceWithColorColumnIds = ((axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.color) || []).map(col => col.id);
        const chartType = chartviz.config.chartType;
        const xAxisColumnIds = ((axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.x) || []).map(col => col.id);
        if ([ChartType.Pie, ChartType.Funnel].includes(chartType)) {
            if (xAxisColumnIds.length && xAxisColumnIds[0] === columnId) {
                return {
                    userDefined: chartViz.vizProp.seriesColors,
                    systemDefined: chartViz.vizProp.systemSeriesColors,
                };
            }
        }
        if (sliceWithColorColumnIds.length &&
            sliceWithColorColumnIds[0] === columnId) {
            return {
                userDefined: chartViz.vizProp.seriesColors,
                systemDefined: chartViz.vizProp.systemSeriesColors,
            };
        }
        return {
            userDefined: null,
            systemDefined: null,
        };
    };
    const getColumnMultiColorSeriesColor = (chartViz, columnId) => {
        var _a;
        const axisConfig = _.first((_a = chartViz === null || chartViz === void 0 ? void 0 : chartViz.config) === null || _a === void 0 ? void 0 : _a.axisConfig) || {};
        const sliceWithColorColumnIds = ((axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.color) || []).map(col => col.id);
        if (sliceWithColorColumnIds.length &&
            sliceWithColorColumnIds[0] === columnId) {
            return {
                userDefined: chartViz.vizProp.multiColorSeriesColors,
                systemDefined: chartViz.vizProp.systemMultiColorSeriesColors,
            };
        }
        return {
            userDefined: null,
            systemDefined: null,
        };
    };
    const getGradient = (chartviz, columnId) => {
        var _a, _b, _c, _d, _e;
        const conditionalFormatting = (_c = (_b = (_a = chartviz.vizProp.columnProperties) === null || _a === void 0 ? void 0 : _a.find(property => property.columnId === columnId)) === null || _b === void 0 ? void 0 : _b.columnProperty) === null || _c === void 0 ? void 0 : _c.conditionalFormatting;
        const rule = (_d = conditionalFormatting === null || conditionalFormatting === void 0 ? void 0 : conditionalFormatting.rows) === null || _d === void 0 ? void 0 : _d.find(rule => rule.backgroundFormatType === BackgroundFormatTypes.Gradient);
        if (rule) {
            return {
                colors: rule.gradientBackgroundAttrs.colors,
                range: rule.gradientBackgroundAttrs.backgroundFormatRange,
                isAutoScaled: (_e = rule.gradientBackgroundAttrs.isAutoScaled) !== null && _e !== void 0 ? _e : null,
            };
        }
        return null;
    };
    columnProperties.forEach(column => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        const { columnId, columnProperty } = column;
        const { advancedProperties } = JSON.parse(columnProperty.customProps || '{}');
        newColumnProperties[columnId] = {
            ...columnProperty,
            kpiColumnProperties: {
                __typename: 'KpiColumnProperties',
                showAbbreviatedPreviousDate: (_b = (_a = columnProperty.kpiColumnProperties) === null || _a === void 0 ? void 0 : _a.showAbbreviatedPreviousDate) !== null && _b !== void 0 ? _b : null,
                abbreviatedPreviousDate: (_d = (_c = columnProperty.kpiColumnProperties) === null || _c === void 0 ? void 0 : _c.abbreviatedPreviousDate) !== null && _d !== void 0 ? _d : null,
                showSparkline: (_f = (_e = columnProperty.kpiColumnProperties) === null || _e === void 0 ? void 0 : _e.showSparkline) !== null && _f !== void 0 ? _f : true,
                showComparisonDate: (_h = (_g = columnProperty.kpiColumnProperties) === null || _g === void 0 ? void 0 : _g.showComparisonDate) !== null && _h !== void 0 ? _h : true,
                showCurrentDateLabel: (_k = (_j = columnProperty.kpiColumnProperties) === null || _j === void 0 ? void 0 : _j.showCurrentDateLabel) !== null && _k !== void 0 ? _k : true,
                showPreviousDateLabel: (_m = (_l = columnProperty.kpiColumnProperties) === null || _l === void 0 ? void 0 : _l.showPreviousDateLabel) !== null && _m !== void 0 ? _m : true,
                showPreviousValue: (_p = (_o = columnProperty.kpiColumnProperties) === null || _o === void 0 ? void 0 : _o.showPreviousValue) !== null && _p !== void 0 ? _p : true,
                handleNullData: (_r = (_q = columnProperty.kpiColumnProperties) === null || _q === void 0 ? void 0 : _q.handleNullData) !== null && _r !== void 0 ? _r : null,
            },
            enableMarkers: (_s = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.enableMarkers) !== null && _s !== void 0 ? _s : null,
            lineStyle: (_t = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.lineStyle) !== null && _t !== void 0 ? _t : null,
            markerStyle: (_u = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.markerStyle) !== null && _u !== void 0 ? _u : null,
            columnColor: {
                userDefined: (_v = getColumnColor(chartviz, columnId, clientState.seriesColors)) !== null && _v !== void 0 ? _v : null,
                systemDefined: (_w = getColumnColor(chartviz, columnId, clientState.systemSeriesColors)) !== null && _w !== void 0 ? _w : null,
            },
            columnDataColors: getColumnDataColors(chartviz, columnId),
            multiColorSeriesColor: getColumnMultiColorSeriesColor(chartviz, columnId),
            gradient: getGradient(chartviz, columnId),
        };
    });
    const newAxisProperties = {};
    const firstAxis = getFirstAxis(chartviz);
    const firstAxisId = (_c = firstAxis === null || firstAxis === void 0 ? void 0 : firstAxis.id) !== null && _c !== void 0 ? _c : null;
    axisProperties.forEach(axis => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const { id, properties } = axis;
        const customProps = JSON.parse(properties.customProps || '{}');
        const { axisSettings, labels, ticks } = (customProps === null || customProps === void 0 ? void 0 : customProps.advancedProperties) || {};
        const { axisName, inheritNF } = axisSettings || {};
        const nameTextStyle = createTextStyle(axisName);
        const labelTextStyle = createTextStyle(labels, [
            (labels === null || labels === void 0 ? void 0 : labels.wrap) ? 'wrap' : null,
        ]);
        const chartType = (_a = chartviz === null || chartviz === void 0 ? void 0 : chartviz.config) === null || _a === void 0 ? void 0 : _a.chartType;
        const isXAxisVertical = [ChartType.Bar, ChartType.StackedBar].includes(chartType);
        let gridlineStyle;
        if (properties.axisType === 'X') {
            gridlineStyle = isXAxisVertical
                ? gridlinesStyles === null || gridlinesStyles === void 0 ? void 0 : gridlinesStyles.yAxis
                : gridlinesStyles === null || gridlinesStyles === void 0 ? void 0 : gridlinesStyles.xAxis;
        }
        else {
            gridlineStyle = isXAxisVertical
                ? gridlinesStyles === null || gridlinesStyles === void 0 ? void 0 : gridlinesStyles.xAxis
                : gridlinesStyles === null || gridlinesStyles === void 0 ? void 0 : gridlinesStyles.yAxis;
        }
        const gridLineEnabled = properties.axisType === 'X'
            ? (_b = gridLinesFlags === null || gridLinesFlags === void 0 ? void 0 : gridLinesFlags.xGridlineEnabled) !== null && _b !== void 0 ? _b : null
            : (_c = gridLinesFlags === null || gridLinesFlags === void 0 ? void 0 : gridLinesFlags.yGridlineEnabled) !== null && _c !== void 0 ? _c : null;
        const isFirstAxis = id === firstAxisId;
        const resolvedNameTextStyle = hasProperties(nameTextStyle)
            ? nameTextStyle
            : getDefaultTextStyle(isFirstAxis, firstAxisId, 'nameTextStyle');
        const resolvedLabelTextStyle = hasProperties(labelTextStyle)
            ? labelTextStyle
            : getDefaultTextStyle(isFirstAxis, firstAxisId, 'labelTextStyle');
        newAxisProperties[id] = {
            inheritNF,
            axisType: properties.axisType,
            customProps: properties.customProps,
            format: properties.format,
            position: properties.isOpposite,
            linkedColumns: properties.linkedColumns,
            name: properties.name,
            yAxisRange: properties.yAxisRange,
            plotLines: null,
            visible: (_d = axisSettings === null || axisSettings === void 0 ? void 0 : axisSettings.visible) !== null && _d !== void 0 ? _d : true,
            isNameVisible: (_e = axisName === null || axisName === void 0 ? void 0 : axisName.isAxisNameVisible) !== null && _e !== void 0 ? _e : true,
            isLabelVisible: (_f = labels === null || labels === void 0 ? void 0 : labels.enabled) !== null && _f !== void 0 ? _f : true,
            nameTextStyle: resolvedNameTextStyle,
            labelTextStyle: resolvedLabelTextStyle,
            labelMaxCharacters: (_g = labels === null || labels === void 0 ? void 0 : labels.maxCharacters) !== null && _g !== void 0 ? _g : '',
            isGridLineEnabled: gridLineEnabled,
            gridlineStyle: hasProperties(gridlineStyle)
                ? {
                    color: gridlineStyle === null || gridlineStyle === void 0 ? void 0 : gridlineStyle.gridLineColor,
                    dashStyle: gridlineStyle === null || gridlineStyle === void 0 ? void 0 : gridlineStyle.gridLineDashStyle,
                    thickness: gridlineStyle === null || gridlineStyle === void 0 ? void 0 : gridlineStyle.gridLineWidth,
                }
                : null,
            dynamicTitle: properties.dynamicTitle,
            intervals: {
                visible: (_h = ticks === null || ticks === void 0 ? void 0 : ticks.showTicks) !== null && _h !== void 0 ? _h : false,
                length: (_j = ticks === null || ticks === void 0 ? void 0 : ticks.tickLength) !== null && _j !== void 0 ? _j : '5',
                thickness: (_k = ticks === null || ticks === void 0 ? void 0 : ticks.tickWidth) !== null && _k !== void 0 ? _k : '1',
                color: (_l = ticks === null || ticks === void 0 ? void 0 : ticks.tickColor) !== null && _l !== void 0 ? _l : '#5E5E5E',
            },
        };
    });
    const newDataLabelProperties = {
        allLabels: chartProperties.allLabels,
        stackLabels: {
            isVisible: chartProperties.showStackedLabels,
            backgroundStyle: hasProperties(stackLabelStyles)
                ? {
                    backgroundColor: stackLabelStyles.backgroundColor,
                    borderColor: stackLabelStyles.borderColor,
                    borderWidth: stackLabelStyles.borderWidth,
                }
                : null,
            labelTextStyle: createTextStyle(stackLabelStyles === null || stackLabelStyles === void 0 ? void 0 : stackLabelStyles.style),
            maxCharacters: (_d = stackLabelStyles === null || stackLabelStyles === void 0 ? void 0 : stackLabelStyles.maxCharacters) !== null && _d !== void 0 ? _d : '',
            align: (_e = stackLabelStyles === null || stackLabelStyles === void 0 ? void 0 : stackLabelStyles.align) !== null && _e !== void 0 ? _e : null,
        },
    };
    const yColumnIds = getYColumnIds(chartviz);
    const firstYColumnId = (_f = yColumnIds[0]) !== null && _f !== void 0 ? _f : null;
    columnProperties.forEach(column => {
        var _a, _b, _c;
        const { columnId } = column;
        const { advancedProperties } = JSON.parse(column.columnProperty.customProps || '{}');
        const dataLabels = (advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.dataLabels) || {};
        const labelTextStyle = createTextStyle(dataLabels === null || dataLabels === void 0 ? void 0 : dataLabels.style);
        const isFirstYColumn = columnId === firstYColumnId;
        const isYColumn = yColumnIds.includes(columnId);
        const resolvedLabelTextStyle = hasProperties(labelTextStyle)
            ? labelTextStyle
            : getDefaultLabelTextStyle(isYColumn, isFirstYColumn, firstYColumnId);
        newDataLabelProperties[columnId] = {
            isVisible: (_a = column.columnProperty.dataLabels) !== null && _a !== void 0 ? _a : null,
            backgroundStyle: hasProperties(dataLabels)
                ? {
                    backgroundColor: dataLabels.backgroundColor,
                    borderColor: dataLabels.borderColor,
                    borderWidth: dataLabels.borderWidth,
                }
                : null,
            filter: hasProperties(dataLabels.filter) ? dataLabels.filter : null,
            align: (_b = dataLabels.align) !== null && _b !== void 0 ? _b : null,
            labelTextStyle: resolvedLabelTextStyle,
            maxCharacters: (_c = dataLabels.maxCharacters) !== null && _c !== void 0 ? _c : '',
        };
    });
    const newTooltipProperties = {
        isVisible: (_g = tooltip === null || tooltip === void 0 ? void 0 : tooltip.enabled) !== null && _g !== void 0 ? _g : true,
        backgroundStyle: createTooltipBackgroundStyle(tooltip),
        labelTextStyle: createTextStyle(tooltip === null || tooltip === void 0 ? void 0 : tooltip.label),
        valueTextStyle: createTextStyle(tooltip === null || tooltip === void 0 ? void 0 : tooltip.value),
        visibleColumns: null,
    };
    const newLegendProperties = {
        enabled: chartProperties.showLegend,
        position: (_h = LEGEND_POSITION_V4_v5_MAP[chartProperties.legendPosition]) !== null && _h !== void 0 ? _h : null,
    };
    let newDisplayProperties = {};
    const getPivotSubtotalsState = (pivotSummariesState) => {
        var _a, _b;
        return {
            showRowTotals: (_a = pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowTotals) !== null && _a !== void 0 ? _a : null,
            showColumnTotals: (_b = pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnTotals) !== null && _b !== void 0 ? _b : null,
        };
    };
    const getPivotGrandTotalsState = (pivotSummariesState) => {
        var _a, _b;
        return {
            showRowGrandTotals: (_a = pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showRowGrandTotals) !== null && _a !== void 0 ? _a : null,
            showColumnGrandTotals: (_b = pivotSummariesState === null || pivotSummariesState === void 0 ? void 0 : pivotSummariesState.showColumnGrandTotals) !== null && _b !== void 0 ? _b : null,
        };
    };
    if (isCustomOrTsHostedChart(chartviz.config.chartType)) {
        const customChartProps = chartProperties.chartSpecific.customProps || '{}';
        newDisplayProperties = JSON.parse(customChartProps);
    }
    else {
        const autoSetBarWidth = _.isNil(answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.width) &&
            _.isNil(answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.padding) &&
            _.isNil(answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.paddingCategories);
        const customProps = JSON.parse(chartSpecific.customProps || '{}');
        const customCompareValue = customProps.customCompare;
        const genericCompareValue = customProps.genericCompareValue;
        const isKPIChartEnhancementFeatureEnabled = isChartSettingsV2Enabled() && isKPIChartEnhancementsEnabled();
        const kpiAxisConfig = _.first((_j = chartviz === null || chartviz === void 0 ? void 0 : chartviz.config) === null || _j === void 0 ? void 0 : _j.axisConfig) || {};
        const firstXAxisColumnId = (_k = ((kpiAxisConfig === null || kpiAxisConfig === void 0 ? void 0 : kpiAxisConfig.x) || [])[0]) === null || _k === void 0 ? void 0 : _k.id;
        const primaryColumnProperty = firstXAxisColumnId
            ? ((_l = columnProperties === null || columnProperties === void 0 ? void 0 : columnProperties.find((cp) => cp.columnId === firstXAxisColumnId)) !== null && _l !== void 0 ? _l : columnProperties === null || columnProperties === void 0 ? void 0 : columnProperties[0])
            : columnProperties === null || columnProperties === void 0 ? void 0 : columnProperties[0];
        updateColumnCustomPropsWithCompareValues(primaryColumnProperty, customCompareValue, genericCompareValue, isKPIChartEnhancementFeatureEnabled);
        if (isKPIChartEnhancementFeatureEnabled && primaryColumnProperty) {
            const primaryColId = primaryColumnProperty.columnId;
            if (newColumnProperties[primaryColId]) {
                newColumnProperties[primaryColId].customProps =
                    primaryColumnProperty.columnProperty.customProps;
            }
        }
        newDisplayProperties = {
            showLabel: (_m = customProps.showLabel) !== null && _m !== void 0 ? _m : true,
            showComparison: (_o = customProps.showComparison) !== null && _o !== void 0 ? _o : true,
            showSparkline: (_p = customProps.showSparkline) !== null && _p !== void 0 ? _p : true,
            showAnalyseChangeCta: (_q = customProps.showAnalyseChangeCta) !== null && _q !== void 0 ? _q : true,
            showAnomalies: (_r = customProps.showAnomalies) !== null && _r !== void 0 ? _r : null,
            isStackedAsPercent: chartSpecific.isStackedAsPercent,
            stackedAsPercentFormat: (_s = chartSpecific.stackedAsPercentFormat) !== null && _s !== void 0 ? _s : DEFAULT_PERCENTAGE_FORMAT,
            axisExtremes: chartProperties.axisExtremes,
            responsiveLayoutDisabled: chartProperties.responsiveLayoutDisabled,
            responsiveLayoutPreference: chartProperties.responsiveLayoutPreference,
            autoSetBarWidth,
            barWidth: autoSetBarWidth ? '' : answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.width,
            barPadding: autoSetBarWidth ? '' : answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.padding,
            groupPadding: autoSetBarWidth
                ? ''
                : answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.paddingCategories,
            markersEnabled: chartSpecific.markersEnabled,
            dataSize: chartProperties.dataSize,
            chart: getChartBackgroundStyle(answerLevelSettings),
            plotArea: getPlotAreaBackgroundStyle(answerLevelSettings),
            seriesColors: clientState.seriesColors,
            systemSeriesColors: clientState.systemSeriesColors,
            systemMultiColorSeriesColors: clientState.systemMultiColorSeriesColors,
            multiColorSeriesColors: clientState.multiColorSeriesColors,
            customColorSelectorArray: clientState.customColorSelectorArray,
            visibleSeriesNames: chartProperties.visibleSeriesNames,
            tooltipConfig: clientState.tooltipConfig,
            regressionLine: {
                enabled: chartProperties.showLinearRegressionLine,
                lineStyle: hasProperties(regression)
                    ? {
                        color: regression === null || regression === void 0 ? void 0 : regression.color,
                        dashStyle: regression === null || regression === void 0 ? void 0 : regression.dashStyle,
                        thickness: regression === null || regression === void 0 ? void 0 : regression.lineWidth,
                    }
                    : null,
            },
            plotBackgroundImage: answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.plotBackgroundImage,
            isZoomed: chartProperties.isZoomed,
            isHeatmapOverlayed: chartSpecific.isHeatmapOverlayed,
            pivotState: chartSpecific.pivotState,
            pivotSubtotalsState: getPivotSubtotalsState(chartSpecific.pivotSummariesState),
            pivotGrandTotalsState: getPivotGrandTotalsState(chartSpecific.pivotSummariesState),
            summaryFormat: chartSpecific.summaryFormat,
            summaryMode: chartSpecific.summaryMode,
            borderStyle: {
                thickness: (_t = answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.borderWidth) !== null && _t !== void 0 ? _t : null,
                color: (_u = answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.borderColor) !== null && _u !== void 0 ? _u : null,
            },
            connectingLines: (_v = answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.connectingLines) !== null && _v !== void 0 ? _v : null,
            plotLines: null,
            nameTextStyle: null,
            labelTextStyle: null,
            labelMaxCharacters: null,
            isGridLineEnabled: null,
            gridlineStyle: null,
            dataFieldArea: chartSpecific.dataFieldArea,
            hidePivotSummaries: chartSpecific.hidePivotSummaries,
            useFlatLayout: (_w = chartSpecific.useFlatLayout) !== null && _w !== void 0 ? _w : null,
            showPivotSummaryPrior: chartSpecific.showPivotSummaryPrior,
            lineColor: answerLevelSettings === null || answerLevelSettings === void 0 ? void 0 : answerLevelSettings.lineColor,
            mapTileType: tileType !== null && tileType !== void 0 ? tileType : null,
            kpiDisplayProperties: (() => {
                var _a, _b;
                const base = chartProperties.kpiDisplayProperties
                    ? {
                        ...DEFAULT_KPI_DISPLAY_PROPERTIES,
                        ...chartProperties.kpiDisplayProperties,
                    }
                    : { ...DEFAULT_KPI_DISPLAY_PROPERTIES };
                const existingKpiColors = (_a = base.kpiDisplayColorArray) !== null && _a !== void 0 ? _a : [];
                const legacyColors = (_b = clientState.customColorSelectorArray) !== null && _b !== void 0 ? _b : [];
                if (existingKpiColors.length < 2 &&
                    legacyColors.length >= 2) {
                    return {
                        ...base,
                        kpiDisplayColorArray: legacyColors.slice(0, 2),
                    };
                }
                return base;
            })(),
            ...(!isKPIChartEnhancementFeatureEnabled && {
                customCompare: customCompareValue !== null && customCompareValue !== void 0 ? customCompareValue : TupleType.PrevAvailable,
                ...(genericCompareValue && {
                    genericCompareValue,
                }),
            }),
        };
    }
    return {
        columnProperties: newColumnProperties,
        axisProperties: newAxisProperties,
        dataLabelProperties: newDataLabelProperties,
        tooltipProperties: newTooltipProperties,
        legendProperties: newLegendProperties,
        displayProperties: newDisplayProperties,
    };
};
export const upgraderFn = (chartviz, customVisualProps, renderPropElements = true) => {
    const { vizProp } = chartviz;
    const validateClientStateV4dot2Schema = ClientStateV4dot2Schema.validate(vizProp);
    if (validateClientStateV4dot2Schema.error) {
        throw new Error(`[validateClientStateV4dot2Schema] : ${validateClientStateV4dot2Schema.error.message}`);
    }
    const clientStateVersion = _.get(vizProp, CLIENT_STATE_KEYS.version);
    if (clientStateVersion !== CLIENT_STATE_VERSIONS.V4dot2) {
        throw new Error(`incompatible client state received in chart upgrade path for v4.2 to v5 with version no:${clientStateVersion}`);
    }
    return upgradeV4Dot2ToV5(chartviz, customVisualProps, renderPropElements);
};
//# sourceMappingURL=v4dot2-to-v5.js.map